import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Bell, ToggleLeft, ToggleRight, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnnouncementsPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', content: '', is_active: true });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/announcements`, { withCredentials: true });
      setAnnouncements(response.data);
    } catch (error) {
      // error handled silently
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/announcements`, formData, { withCredentials: true });
      toast.success('Announcement created successfully');
      setIsDialogOpen(false);
      setFormData({ title: '', content: '', is_active: true });
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create announcement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await axios.delete(`${API}/announcements/${id}`, { withCredentials: true });
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) { toast.error('Failed to delete'); }
  };

  const toggleNewsletterOptIn = async (announcement) => {
    const next = !announcement.include_in_newsletter;
    try {
      await axios.patch(
        `${API}/admin/newsletter-opt-in/announcements/${announcement.id}`,
        { include_in_newsletter: next },
        { withCredentials: true }
      );
      toast.success(next ? 'Added to newsletter pipeline' : 'Removed from newsletter pipeline');
      fetchAnnouncements();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update newsletter flag');
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="announcements-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">Announcements</h1>
          <p className="text-slate-600 mt-1">Create and manage member announcements</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#002249] hover:bg-[#0a3570] rounded-sm" data-testid="create-announcement-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Announcement</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input 
                  value={formData.title} 
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
                  required 
                  className="rounded-sm" 
                  data-testid="announcement-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Content *</Label>
                <Textarea 
                  value={formData.content} 
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })} 
                  required 
                  rows={4} 
                  className="rounded-sm" 
                  data-testid="announcement-content-input"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch 
                  checked={formData.is_active} 
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} 
                  data-testid="announcement-active-switch"
                />
                <Label>Active (visible to members)</Label>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-sm">Cancel</Button>
                <Button type="submit" className="bg-[#002249] hover:bg-[#0a3570] rounded-sm" data-testid="announcement-submit-btn">Create Announcement</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Announcements List */}
      <div className="space-y-4">
        {announcements.map((announcement, index) => (
          <div 
            key={announcement.id} 
            className={`bg-white border rounded-sm p-4 ${announcement.is_active ? 'border-green-200' : 'border-slate-200'}`}
            data-testid={`announcement-${index}`}
          >
            <div className="flex justify-between items-start">
              <div className="flex items-start space-x-3">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${announcement.is_active ? 'bg-green-100' : 'bg-slate-100'}`}>
                  <Bell className={`w-5 h-5 ${announcement.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-[#002249]">{announcement.title}</h3>
                  <p className="text-sm text-slate-600 mt-1">{announcement.content}</p>
                  <p className="text-xs text-slate-400 mt-2">
                    Created: {new Date(announcement.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost" size="sm"
                  onClick={() => toggleNewsletterOptIn(announcement)}
                  className={`${announcement.include_in_newsletter ? 'text-[#E63946] hover:text-[#c02a35]' : 'text-slate-400 hover:text-[#E63946]'}`}
                  data-testid={`newsletter-toggle-announcement-${index}`}
                  title={announcement.include_in_newsletter ? 'In newsletter pipeline — click to remove' : 'Add to newsletter pipeline'}
                >
                  <Mail className={`w-4 h-4 ${announcement.include_in_newsletter ? 'fill-[#E63946]/15' : ''}`} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(announcement.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-announcement-${index}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
                {announcement.is_active ? (
                  <span className="inline-flex items-center text-xs text-green-600">
                    <ToggleRight className="w-4 h-4 mr-1" />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center text-xs text-slate-400">
                    <ToggleLeft className="w-4 h-4 mr-1" />
                    Inactive
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
        {announcements.length === 0 && (
          <div className="bg-white border border-slate-200 p-12 text-center text-slate-500 rounded-sm">
            <Bell className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p>No announcements yet. Create your first announcement!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementsPage;
